import React, {useContext} from 'react';
import {AppContext} from '../../contexts/AppContext';
import Skeleton from 'react-loading-skeleton';
import {translate} from '../../helpers/base';
import {getOptionItems, isGroupSelected} from '../../helpers/filters';
const FilterPill = ({filterType, filterValue, label, onRemove}) => (
    <label className="filter-pill active">
        {label}
        <span className="close" onClick={() => onRemove(filterType, filterValue)}></span>
    </label>
);

const includedTypes = [
    'accoFeatures',
    'accoType',
    'animals',
    'awards',
    'discount',
    'experienceWorlds',
    'locations',
    'region',
    'state',
    'mealPlans',
    'roomTypes',
    'price',
    'specializations',
    'topics',
    'petsAllowed',
    'accessible',
    'distance',
    'q'
];

export const useHasActiveFilters = () => {
    const {selectedFilters} = useContext(AppContext);

    if (selectedFilters.accoType) {
        return true;
    }

    return Object.keys(selectedFilters).some(filterType => {
        const filterValues = selectedFilters[filterType] ?? [];
        const values = Array.isArray(filterValues) ? filterValues : [filterValues];
        return includedTypes.includes(filterType) && values.length > 0;
    });
};

const removeSingleFilter = (
    filterType,
    filterValue,
    handleFilterChange,
    handleAccommodationTypeChange
) => {
    if (filterType === 'accoType') {
        handleAccommodationTypeChange(null);
    } else {
        handleFilterChange(filterType, filterValue, false);
    }
};

const isOptionCoveredBySelectedGroup = (option, allFilters, selectedByType) => {

    if (!allFilters?.length)
        return false;

    const itemsOfOption = getOptionItems(option);

    return allFilters.some(groupOption => {

        if (!groupOption.isGroup || groupOption === option)
            return false;

        if (!isGroupSelected(groupOption, selectedByType))
            return false;

        const groupItems = groupOption.items || [];
        return itemsOfOption.every(item =>
            groupItems.some(groupItem => groupItem.type === item.type && groupItem.id === item.id)
        );
    });
};

const getFilterLabel = (filterType, filterValue, filters) => {
    let filterOption = filters.find(item => !item.isGroup && item.id === filterValue) || null;

    // If not found as a normal filter, try to find it inside group items
    if (!filterOption) {
        const groupWithItem = filters.find(
            f => f.isGroup && f.items.some(sub => sub.type === filterType && sub.id === filterValue)
        );

        if (groupWithItem) {
            filterOption = groupWithItem.items.find(
                sub => sub.type === filterType && sub.id === filterValue
            );
        }
    }

    let label = filterOption?.label || filterValue;

    // Extract first part of location if applicable
    if (filterType === 'q' && typeof label === 'string' && label.includes(',')) {
        label = label.split(',')[0];
    }

    // Handle price filter label
    if (
        filterType === 'price' &&
        typeof label === 'string' &&
        label.includes('-')
    ) {
        const [minPrice, maxPrice] = label.split('-').map(price => price.trim());
        return `€ ${minPrice} - ${maxPrice}`;
    }

    if (filterType === 'petsAllowed') {
        return translate('accommodation-overview.filter.misc.petsAllowed');
    }

    if (filterType === 'discount') {
        return translate('accommodation-overview.filter.misc.discount.activeFilterLabel');
    }

    /*
    if (filterType === 'accessible') {
        return translate(
            'accommodation-overview.filter.misc.accessibleFacilities'
        );
    }
    */

    if (filterType === 'distance') {
        return `${label} km`;
    }

    return label;
};

// selectedFilters => { type -> Set(ids) }
const toSelectedByType = (selectedFilters = {}) => {
    const map = {};
    Object.entries(selectedFilters).forEach(([type, value]) => {
        const vals = Array.isArray(value) ? value : [value];
        map[type] = new Set(vals);
    });
    return map;
};

/**
 * Get all groups that are effectively selected (not covered by larger groups)
 */
const getEffectiveSelectedGroups = (selectedFilters, filters) => {
    const selectedByType = toSelectedByType(selectedFilters);

    const initialSelectedGroups = filters.filter(
        f => f.isGroup && isGroupSelected(f, selectedByType)
    );

    // Remove groups that are covered by another selected group
    return initialSelectedGroups.filter(
        g => !isOptionCoveredBySelectedGroup(g, initialSelectedGroups, selectedByType)
    );
};

/**
 * Build the pills:
 * - Add a pill for each selected effective group
 * - Hide individual items that belong to those groups
 */
const buildFilterPills = ({
    selectedFilters,
    includedTypes,
    filters,
    onRemoveFilter,
}) => {
    const filterPills = [];
    const regionLabels = new Set();

    // Identify effective selected groups
    const selectedGroups = getEffectiveSelectedGroups(selectedFilters, filters);

    // Track items that belong to selected groups (so we can hide them)
    const hiddenItemKeys = new Set();
    selectedGroups.forEach(group => {
        if (Array.isArray(group.items)) {
            group.items.forEach(item => {
                hiddenItemKeys.add(`${item.type}-${item.id}`);
            });
        }
    });

    // Add pills for selected groups
    selectedGroups.forEach(group => {
        if (!group.label) return;
        filterPills.push(
            <FilterPill
                key={`group-${group.id}`}
                filterType="group"
                filterValue={group.id}
                label={group.label}
                onRemove={onRemoveFilter}
            />
        );
    });

    // Collect all region + state labels (for dedup logic vs 'q')
    ['region', 'state'].forEach(filterType => {
        if (!selectedFilters[filterType]) return;
        const values = Array.isArray(selectedFilters[filterType])
            ? selectedFilters[filterType]
            : [selectedFilters[filterType]];
        values.forEach(value => {
            const label = getFilterLabel(filterType, value, filters);
            if (label) regionLabels.add(label);
        });
    });

    // Build individual pills (excluding those covered by a group)
    Object.keys(selectedFilters)
        .reverse()
        .forEach(filterType => {
            if (!includedTypes.includes(filterType)) {
                return;
            }

            const filterValues = selectedFilters[filterType];
            if (!filterValues) {
                return;
            }

            const values = Array.isArray(filterValues) ? filterValues : [filterValues];

            values.forEach(filterValue => {
                // Skip items that belong to a selected group
                if (hiddenItemKeys.has(`${filterType}-${filterValue}`)) {
                    return;
                }

                const filterLabel = getFilterLabel(filterType, filterValue, filters);
                if (!filterLabel) return;

                // Exclude pill for 'q' if its label matches a region label
                if (filterType === 'q' && regionLabels.has(filterLabel)) {
                    return;
                }

                filterPills.push(
                    <FilterPill
                        key={`${filterType}-${filterValue}`}
                        filterType={filterType}
                        filterValue={filterValue}
                        label={filterLabel}
                        onRemove={onRemoveFilter}
                    />
                );
            });
        });

    return filterPills;
};

const ActiveFilters = () => {
    const {
        selectedFilters,
        handleFilterChange,
        handleAccommodationTypeChange,
        isLoading,
        isMobile,
        allFilters: filters
    } = useContext(AppContext);

    const handleRemoveFilter = (filterType, filterValue) => {
        // When removing a group pill, remove all its items
        if (filterType === 'group') {
            const group = filters.find(f => f.isGroup && f.id === filterValue);
            if (group && Array.isArray(group.items)) {
                group.items.forEach(item => {
                    removeSingleFilter(
                        item.type,
                        item.id,
                        handleFilterChange,
                        handleAccommodationTypeChange
                    );
                });
            }
            return;
        }

        // Normal single filter removal
        removeSingleFilter(
            filterType,
            filterValue,
            handleFilterChange,
            handleAccommodationTypeChange
        );
    };

    if (isLoading) {
        return (
            <Skeleton
                count={isMobile ? 1 : 2}
                inline={true}
                width={150}
                height={isMobile ? 35 : 40}
                borderRadius={20}
                style={{marginRight: 10}}
            />
        );
    }

    return buildFilterPills({
        selectedFilters,
        includedTypes,
        filters,
        onRemoveFilter: handleRemoveFilter,
    });
};

export default ActiveFilters;
